import { BASE_URL } from "./variables";

/**
 * FMS POS & Assignment API (dropdowns + add-pos / add-worker-assignment).
 * These endpoints write the same records as the .NET POS and Assigned Employee/Vendor tabs.
 * Same FMS base URL as the rest of the app (staging locally); the company comes from the token.
 */
const FMS_POS_BASE_URL = BASE_URL;

export class FmsApiError extends Error {
  constructor(status, message, details = []) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

function getToken() {
  try {
    return localStorage.getItem('businessAuthToken') || localStorage.getItem('authToken');
  } catch {
    return null;
  }
}

async function call(path, init = {}) {
  const token = getToken();
  const response = await fetch(`${FMS_POS_BASE_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new FmsApiError(response.status, body?.error || `Request failed (${response.status})`, body?.details || []);
  }
  return body;
}

/** "Message: detail 1; detail 2" for showing an FmsApiError to the user */
export function describeFmsError(error) {
  if (!error) return 'Request failed';
  const details = Array.isArray(error.details) && error.details.length ? `: ${error.details.join('; ')}` : '';
  return `${error.message || 'Request failed'}${details}`;
}

// --- Dropdowns ---

/** Active service types of the token's company: [{ ServiceTypeId, Code, Name, CategoryID, CategoryName, Status }] */
export const getBusinessServiceTypes = () =>
  call('/get-business-service-types').then(r => r.data || []);

/** Workers and vendors that can be assigned: [{ WorkerId, Name, IsVendor, ProviderType: 'Worker' | 'Vendor', ... }] */
export const getAssignableWorkers = (providerType) =>
  call(`/get-assignable-workers${providerType ? `?providerType=${providerType}` : ''}`).then(r => r.data || []);

/** The company's burden rates: [{ BurdenRateId, Name, Percent }] */
export const getBurdenRates = () =>
  call('/get-burden-rates').then(r => r.data || []);

// --- Writes ---

/**
 * Creates one POS (authorization). Type 3 = dollars, so AuthorizedUnits is a dollar amount.
 * Every call creates a new POS (no duplicate check). Returns { success, message, AuthorizeServiceID }.
 */
export const addPos = (request) =>
  call('/add-pos', { method: 'POST', body: JSON.stringify(request) });

/**
 * Assigns a worker or vendor to the participant for a service type. Returns
 * { success, Id, alreadyExisted, PayRate, BillRate, BurdenPercent, ... }; an identical active
 * assignment is returned with alreadyExisted: true instead of being duplicated.
 */
export const addWorkerAssignment = (request) =>
  call('/add-worker-assignment', { method: 'POST', body: JSON.stringify(request) });
